import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { AKTY, TEMATY, TEMAT_PO_SLUGU } from '../data/przepisy';
import { PATENT_PAGES, breadcrumbJsonLd, faqJsonLd } from '../seo/siteMeta';
import JsonLd from '../seo/JsonLd';
import Faq from './Faq';
import styles from '../css/modules/Nauka.module.css';

/**
 * Dział /przepisy - rozdroże i strony tematyczne.
 *
 * Jeden komponent obsługuje oba widoki: bez parametru w adresie renderuje hub,
 * z parametrem konkretny temat. Treść siedzi w src/data/przepisy.js, więc
 * dopisanie tematu nie wymaga nowego pliku - tylko wpisu w danych, w ROUTE_META
 * i w scripts/seo-routes.mjs.
 */

const ZASTRZEZENIE =
  'To streszczenie na potrzeby nauki, a nie tekst przepisu. Wiążąca jest treść ' +
  'aktu prawnego w brzmieniu obowiązującym oraz decyzja organizatora egzaminu.';

const Hub = () => (
  <div className={styles.wrap}>
    <JsonLd data={breadcrumbJsonLd([
      { name: 'Strona główna', path: '/' },
      { name: 'Przepisy', path: '/przepisy' },
    ])} />

    <nav className={styles.breadcrumbs}>
      <Link to="/">Strona główna</Link> / Przepisy
    </nav>

    <h1 className={styles.h1}>Przepisy żeglarskie w pigułce</h1>
    <p className={styles.lead}>
      Pięć obszarów, z których najczęściej pyta się na egzaminie: kiedy patent jest
      w ogóle wymagany, kto komu ustępuje, jak czytać oznakowanie akwenu, co znaczą
      sygnały dźwiękowe i jakie wyposażenie musi mieć jacht.
    </p>

    <div className={styles.kafle}>
      {TEMATY.map(t => (
        <Link key={t.slug} to={`/przepisy/${t.slug}`} className={styles.kafel}>
          <span className={styles.kafelUdzial}>Przepisy</span>
          <span className={styles.kafelNazwa}>{t.nazwa}</span>
          <span className={styles.kafelOpis}>{t.description}</span>
        </Link>
      ))}
    </div>

    <h2 className={styles.h2}>Na czym to się opiera</h2>
    <div className={styles.tresc}>
      <ul>
        {AKTY.map(a => (
          <li key={a.skrot}>
            <strong>{a.nazwa}</strong> - {a.zakres}.
          </li>
        ))}
      </ul>
      <p className={styles.info}>{ZASTRZEZENIE}</p>
    </div>

    <div className={styles.akcje}>
      <Link className={styles.cta} to={`/egzamin/${PATENT_PAGES[0].slug}`}>
        Sprawdź się na egzaminie próbnym
      </Link>
    </div>
  </div>
);

const Temat = ({ temat }) => (
  <div className={styles.wrap}>
    <JsonLd data={breadcrumbJsonLd([
      { name: 'Strona główna', path: '/' },
      { name: 'Przepisy', path: '/przepisy' },
      { name: temat.nazwa, path: `/przepisy/${temat.slug}` },
    ])} />
    <JsonLd data={faqJsonLd(temat.faq)} />

    <nav className={styles.breadcrumbs}>
      <Link to="/">Strona główna</Link> / <Link to="/przepisy">Przepisy</Link> / {temat.nazwa}
    </nav>

    <h1 className={styles.h1}>{temat.h1}</h1>
    <p className={styles.lead}>{temat.lead}</p>

    <div className={styles.tresc}>
      {temat.sekcje.map(s => (
        <section key={s.naglowek}>
          <h2>{s.naglowek}</h2>
          {s.akapity.map(a => <p key={a.slice(0, 40)}>{a}</p>)}
          {s.lista && (
            <>
              <p><strong>{s.lista.tytul}:</strong></p>
              <ul>
                {s.lista.punkty.map(p => <li key={p.slice(0, 40)}>{p}</li>)}
              </ul>
            </>
          )}
        </section>
      ))}
    </div>

    <h2 className={styles.h2}>Najczęstsze pytania</h2>
    <Faq items={temat.faq} />

    <p className={styles.zastrzezenie}>{ZASTRZEZENIE}</p>

    <div className={styles.akcje}>
      <Link className={styles.cta} to={`/egzamin/${PATENT_PAGES[0].slug}`}>
        Sprawdź się na egzaminie próbnym
      </Link>
      <Link className={styles.ctaDrugi} to="/przepisy">Pozostałe działy przepisów</Link>
    </div>

    <div className={styles.sasiedzi}>
      {TEMATY.filter(t => t.slug !== temat.slug).map(t => (
        <Link key={t.slug} to={`/przepisy/${t.slug}`} className={styles.sasiad}>
          {t.nazwa}
        </Link>
      ))}
    </div>
  </div>
);

const Przepisy = () => {
  const { temat: slug } = useParams();
  if (!slug) return <Hub />;

  const temat = TEMAT_PO_SLUGU[slug];
  if (!temat) {
    return (
      <div className={styles.wrap}>
        <h1 className={styles.h1}>Nie znamy takiego działu przepisów</h1>
        <Link className={styles.cta} to="/przepisy">Wróć do spisu</Link>
      </div>
    );
  }
  return <Temat temat={temat} />;
};

export default Przepisy;
